/**
 * Report pages that draw themselves: the cover (report, client, period, who
 * prepared it) and the contents (page titles, always current). Plus the small
 * text formatter narrative pages use: `# heading`, `- bullet`, `**bold**`.
 */

import { createContext, useContext, type CSSProperties, type ReactNode } from 'react';

import { PRESET_LABELS, rangeLabel } from './format.ts';
import type { Page } from './reports-api.ts';

export interface CoverFacts {
  reportName: string;
  clientName: string;
  periodLabel: string;
  /** The agency: its name, and its own logo and accent when set. */
  preparedBy: string;
  logo?: string | null | undefined;
  accent?: string | null | undefined;
  /** The client's website, for title widgets that show it. */
  clientUrl?: string | null | undefined;
}

/** The client already heads the cover, so "Flux · Monthly report" prints as "Monthly report". */
function withoutClient(reportName: string, clientName: string): string {
  const prefix = `${clientName} · `;
  return clientName && reportName.startsWith(prefix) ? reportName.slice(prefix.length) : reportName;
}

/** Everything a cover prints, from the report, its client, the resolved period and the agency's brand. */
export function coverFacts(input: {
  reportName: string;
  clientName: string;
  clientUrl?: string | null | undefined;
  period: { from: string; to: string } | null;
  preset: string;
  branding: { display_name: string; logo: string | null; accent: string | null };
}): CoverFacts {
  return {
    reportName: input.reportName,
    clientName: input.clientName,
    clientUrl: input.clientUrl,
    periodLabel: input.period
      ? rangeLabel(input.period)
      : (PRESET_LABELS[input.preset] ?? 'This period'),
    preparedBy: input.branding.display_name,
    logo: input.branding.logo,
    accent: input.branding.accent,
  };
}

export function CoverPage({ facts }: { facts: CoverFacts }): ReactNode {
  return (
    <section
      className="rpa-cover"
      aria-label="Cover page"
      style={facts.accent ? ({ '--cover-accent': facts.accent } as CSSProperties) : undefined}
    >
      {facts.logo ? (
        <img className="rpa-cover-logo" src={facts.logo} alt={facts.preparedBy} />
      ) : (
        <p className="rpa-cover-agency">{facts.preparedBy}</p>
      )}
      <div className="rpa-cover-main">
        <p className="rpa-cover-client">{facts.clientName}</p>
        <h1>{withoutClient(facts.reportName, facts.clientName)}</h1>
        <p className="rpa-cover-period">{facts.periodLabel}</p>
      </div>
      <p className="rpa-cover-by">Prepared by {facts.preparedBy}</p>
    </section>
  );
}

/** The report's pages, for widgets that list them (Table of Contents). */
export const ReportPagesContext = createContext<Page[]>([]);
export const useReportPages = (): Page[] => useContext(ReportPagesContext);

/** Numbered titles of the widget pages; covers and contents pages are not listed. */
export function PageList({ pages }: { pages: Page[] }): ReactNode {
  const listed = pages
    .map((page, index) => ({ page, number: index + 1 }))
    .filter(({ page }) => (page.settings.layout ?? 'grid') === 'grid');
  return (
    <ol className="rpa-toc">
      {listed.map(({ page, number }) => (
        <li key={page.id ?? number}>
          <span>{page.title}</span>
          <span className="rpa-dots" aria-hidden="true" />
          <span className="mono">{number}</span>
        </li>
      ))}
    </ol>
  );
}

export function ContentsPage({ pages }: { pages: Page[] }): ReactNode {
  const listed = pages
    .map((page, index) => ({ page, number: index + 1 }))
    .filter(({ page }) => (page.settings.layout ?? 'grid') === 'grid');
  return (
    <section className="rpa-contents" aria-label="Contents">
      <h2>Contents</h2>
      <ol>
        {listed.map(({ page, number }) => (
          <li key={page.id ?? number}>
            <span>{page.title}</span>
            <span className="rpa-dots" aria-hidden="true" />
            <span className="mono">{number}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function inline(text: string): ReactNode[] {
  return text
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, index) =>
      part.startsWith('**') && part.endsWith('**') ? (
        <strong key={index}>{part.slice(2, -2)}</strong>
      ) : (
        <span key={index}>{part}</span>
      ),
    );
}

/** Narrative text: lines starting `# ` are headings, `- ` bullets, the rest paragraphs. */
export function FormattedText({ text }: { text: string }): ReactNode {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length > 0) {
      blocks.push(
        <ul key={`ul-${blocks.length}`}>
          {bullets.map((item, index) => (
            <li key={index}>{inline(item)}</li>
          ))}
        </ul>,
      );
      bullets = [];
    }
  };
  for (const raw of text.split('\n')) {
    const line = raw.trimEnd();
    if (line.startsWith('- ')) {
      bullets.push(line.slice(2));
      continue;
    }
    flush();
    if (line.startsWith('# ')) blocks.push(<h4 key={blocks.length}>{inline(line.slice(2))}</h4>);
    else if (line.trim()) blocks.push(<p key={blocks.length}>{inline(line)}</p>);
  }
  flush();
  return <>{blocks}</>;
}
